import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
  addDoc,
  arrayUnion,
  arrayRemove,
  deleteDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { sanitizeInput } from '../config/security';

/**
 * Fetches an item by ID
 * @param {string} itemId - The ID of the item to fetch
 * @returns {Promise<Object>} - The item data
 */
export const getItemById = async (itemId) => {
  try {
    const docRef = doc(db, "items", itemId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      throw new Error("Item not found");
    }
  } catch (error) {
    console.error("Error getting item:", error);
    throw error;
  }
};

/**
 * Fetches all items
 * @param {Object} filters - Optional filters to apply
 * @returns {Promise<Array>} - Array of items
 */
export const getAllItems = async (filters = {}) => {
  try {
    let itemsQuery = collection(db, "items");
    
    // Apply filters
    if (filters.status) {
      itemsQuery = query(itemsQuery, where("status", "==", filters.status));
    }
    
    if (filters.category) {
      itemsQuery = query(itemsQuery, where("category", "==", filters.category));
    }
    
    const querySnapshot = await getDocs(itemsQuery);
    const items = [];
    
    querySnapshot.forEach((doc) => {
      items.push({ id: doc.id, ...doc.data() });
    });
    
    return items;
  } catch (error) {
    console.error("Error getting items:", error);
    throw error;
  }
};

/**
 * Creates a new item
 * @param {Object} itemData - The item data
 * @param {File} imageFile - Optional image file for the item
 * @returns {Promise<Object>} - The created item
 */
export const createItem = async (itemData, imageFile = null) => {
  try {
    // Create new item document
    const newItemRef = doc(collection(db, "items"));
    const itemId = newItemRef.id;

    // Generate QR code URL for this item
    const baseUrl = window.location.origin;
    const qrCodeUrl = `${baseUrl}/item/${itemId}`;

    // Sanitize text inputs to prevent XSS
    let itemToSave = {
      ...itemData,
      name: sanitizeInput.text(itemData.name || ''),
      category: sanitizeInput.text(itemData.category || ''),
      description: sanitizeInput.text(itemData.description || ''),
      unit: itemData.unit ? sanitizeInput.text(itemData.unit) : undefined,
      status: itemData.status || "Available",
      currentHolderId: null,
      qrCodeUrl: qrCodeUrl,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // For consumables, ensure numeric fields are properly handled
    if (itemData.itemType === 'consumable') {
      itemToSave.initialStock = parseFloat(itemData.initialStock) || 0;
      itemToSave.currentStock = parseFloat(itemData.currentStock) || 0;
      itemToSave.lowStockAlert = itemData.lowStockAlert ? parseFloat(itemData.lowStockAlert) : null;
      itemToSave.costPerUnit = itemData.costPerUnit ? parseFloat(itemData.costPerUnit) : null;
    }
    
    // Create a placeholder for image URL - we'll update this after saving the item
    itemToSave.imageUrl = null;
    
    // Save the item first without the image
    await setDoc(newItemRef, itemToSave);
    
    // Then handle image upload if provided (async after saving the item data)
    if (imageFile) {
      try {
        // Validate file type
        const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!validImageTypes.includes(imageFile.type)) {
          throw new Error(`Unsupported file type: ${imageFile.type}. Please upload a JPEG, PNG, GIF, or WebP image.`);
        }
        
        // Validate file size (5MB max)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (imageFile.size > maxSize) {
          throw new Error(`File is too large. Maximum size is 5MB.`);
        }
        
        // Create a unique filename with timestamp to avoid overwriting existing files
        const fileExtension = imageFile.name.split('.').pop().toLowerCase();
        const filename = `item_${itemId}_${Date.now()}.${fileExtension}`;

        // Create storage reference
        const storageRef = ref(storage, `item_images/${filename}`);
        
        // Log the upload attempt
        console.log(`Attempting to upload image for item ${itemId} to path: item_images/${filename}`, {
          file: {
            name: imageFile.name,
            type: imageFile.type,
            size: imageFile.size
          }
        });

        // Perform the actual upload (no metadata to avoid 412 issues)
        const uploadTask = await uploadBytes(storageRef, imageFile);
        console.log('Upload successful:', {
          metadata: uploadTask.metadata,
          ref: uploadTask.ref
        });
        
        // Get download URL
        const imageUrl = await getDownloadURL(storageRef);
        console.log('Image URL obtained:', imageUrl);
        
        // Update the item with the image URL
        const itemRef = doc(db, "items", itemId);
        await updateDoc(itemRef, { 
          imageUrl: imageUrl,
          imageUpdatedAt: new Date().toISOString()
        });
        console.log('Item document updated with image URL');
        
        // Update return object with image URL
        itemToSave.imageUrl = imageUrl;
      } catch (storageError) {
        const errorMessage = `Storage error (item saved without image): ${storageError.message || 'Unknown error'}`;
        console.error(errorMessage, {
          error: storageError,
          stack: storageError.stack,
          itemId: itemId,
          fileName: imageFile?.name
        });
        
        // Update the item to indicate image upload failure
        try {
          const itemRef = doc(db, "items", itemId);
          await updateDoc(itemRef, { 
            imageUploadError: errorMessage,
            imageUploadErrorAt: new Date().toISOString()
          });
        } catch (updateError) {
          console.error('Failed to update item with image error:', updateError);
        }
        
        // Re-throw the error to be handled by the caller
        throw new Error(`Image upload failed: ${storageError.message}`);
      }
    }
    
    // Return the item data (with or without image URL)
    return { id: itemId, ...itemToSave };
  } catch (error) {
    console.error("Error creating item:", error);
    throw error;
  }
};

/**
 * Updates an existing item
 * @param {string} itemId - The ID of the item to update
 * @param {Object} itemData - The updated item data
 * @param {File} imageFile - Optional new image file
 * @returns {Promise<Object>} - The updated item
 */
export const updateItem = async (itemId, itemData, imageFile = null) => {
  try {
    const itemRef = doc(db, "items", itemId);
    const itemSnap = await getDoc(itemRef);

    if (!itemSnap.exists()) {
      throw new Error("Item not found");
    }

    const existingItem = itemSnap.data();

    // Sanitize text inputs to prevent XSS
    let updatedItem = {
      ...itemData,
      name: sanitizeInput.text(itemData.name || ''),
      category: sanitizeInput.text(itemData.category || ''),
      description: sanitizeInput.text(itemData.description || ''),
      unit: itemData.unit ? sanitizeInput.text(itemData.unit) : undefined,
      updatedAt: new Date().toISOString()
    };

    // For consumables, ensure numeric fields are properly handled
    if (itemData.itemType === 'consumable') {
      updatedItem.initialStock = parseFloat(itemData.initialStock) || 0;
      updatedItem.currentStock = parseFloat(itemData.currentStock) || 0;
      updatedItem.lowStockAlert = itemData.lowStockAlert ? parseFloat(itemData.lowStockAlert) : null;
      updatedItem.costPerUnit = itemData.costPerUnit ? parseFloat(itemData.costPerUnit) : null;
    }
    
    // Keep existing image if no new one is provided
    if (!imageFile && existingItem.imageUrl) {
      updatedItem.imageUrl = existingItem.imageUrl;
    }
    
    // Update the item document first
    await updateDoc(itemRef, updatedItem);
    
    // Handle image upload separately after document update
    if (imageFile) {
      try {
        // Validate file type
        const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!validImageTypes.includes(imageFile.type)) {
          throw new Error(`Unsupported file type: ${imageFile.type}. Please upload a JPEG, PNG, GIF, or WebP image.`);
        }
        
        // Validate file size (5MB max)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (imageFile.size > maxSize) {
          throw new Error(`File is too large. Maximum size is 5MB.`);
        }
        
        // Create a clean filename - can help avoid path issues
        const fileExtension = imageFile.name.split('.').pop().toLowerCase();
        const timestamp = new Date().getTime();
        const filename = `item_${itemId}_${timestamp}.${fileExtension}`;
        
        // Create storage reference with explicit path to avoid issues
        const storageRef = ref(storage, `item_images/${filename}`);
        
        // Log the upload attempt
        console.log(`Attempting to upload updated image for item ${itemId} to path: item_images/${filename}`, {
          file: {
            name: imageFile.name,
            type: imageFile.type,
            size: imageFile.size
          }
        });

        // Perform the actual upload (no metadata to avoid 412 issues)
        const uploadTask = await uploadBytes(storageRef, imageFile);
        console.log('Upload successful for update:', {
          metadata: uploadTask.metadata,
          ref: uploadTask.ref
        });
        
        // Get download URL
        const imageUrl = await getDownloadURL(storageRef);
        console.log('Updated image URL obtained:', imageUrl);
        
        // Update the item with the new image URL and clear any previous upload errors
        await updateDoc(itemRef, { 
          imageUrl: imageUrl,
          imageUpdatedAt: new Date().toISOString(),
          imageUploadError: null,
          imageUploadErrorAt: null
        });
        console.log('Item document updated with new image URL');
        
        // Update return value
        updatedItem.imageUrl = imageUrl;
        
        // Delete old image in the background if it exists and is different from the new one
        if (existingItem.imageUrl && existingItem.imageUrl !== imageUrl) {
          try {
            // Extract the filename from the image URL
            const oldImageUrl = existingItem.imageUrl;
            const oldImagePath = oldImageUrl.split('item_images%2F')[1]?.split('?')[0];
            
            if (oldImagePath) {
              console.log(`Attempting to delete old image: ${oldImagePath}`);
              const oldImageRef = ref(storage, `item_images/${oldImagePath}`);
              
              // Delete the old image
              await deleteObject(oldImageRef);
              console.log('Old image deleted successfully');
              
              // Also delete any thumbnails if they exist
              try {
                const oldImageName = oldImagePath.split('.')[0];
                const oldThumbnailRef = ref(storage, `item_images/thumbnails/${oldImageName}_200x200.${fileExtension}`);
                await deleteObject(oldThumbnailRef).catch(e => {
                  console.log('No thumbnail to delete or error deleting thumbnail:', e);
                });
              } catch (thumbnailError) {
                console.warn('Error deleting thumbnail:', thumbnailError);
              }
            }
          } catch (deleteError) {
            console.warn('Error deleting old image:', deleteError);
            // Don't fail the whole operation if deletion of old image fails
          }
        }
      } catch (storageError) {
        console.error("Storage error (item updated but image wasn't changed):", storageError);
        console.error("Error details:", JSON.stringify(storageError));
        // The text data is already updated, just the image failed
      }
    }
    
    return { id: itemId, ...updatedItem };
  } catch (error) {
    console.error("Error updating item:", error);
    throw error;
  }
};

/**
 * Deletes an item
 * @param {string} itemId - The ID of the item to delete
 * @returns {Promise<void>}
 */
export const deleteItem = async (itemId) => {
  try {
    // Check if item exists
    const itemRef = doc(db, "items", itemId);
    const itemSnap = await getDoc(itemRef);
    
    if (!itemSnap.exists()) {
      throw new Error("Item not found");
    }
    
    const itemData = itemSnap.data();
    
    // If item is checked out, clean up the student's checked out items list
    if (itemData.status === "Checked Out" && itemData.currentHolderId) {
      try {
        const studentRef = doc(db, "students", itemData.currentHolderId);
        const studentSnap = await getDoc(studentRef);
        
        if (studentSnap.exists()) {
          await updateDoc(studentRef, {
            checkedOutItems: arrayRemove(itemId),
            updatedAt: new Date().toISOString()
          });
          console.log(`Removed item ${itemId} from student ${itemData.currentHolderId}'s checked out items`);
        }
      } catch (error) {
        console.error("Error updating student's checked out items during item deletion:", error);
        // Continue with deletion even if student update fails
      }
    }
    
    // Delete image if exists
    if (itemData.imageUrl) {
      try {
        // Extract the filename from the image URL
        const imageUrl = itemData.imageUrl;
        const imagePath = imageUrl.split('item_images%2F')[1]?.split('?')[0];
        
        if (imagePath) {
          const storageRef = ref(storage, `item_images/${imagePath}`);
          console.log(`Attempting to delete image at path: item_images/${imagePath}`);
          await deleteObject(storageRef);
          console.log('Image deleted successfully');
        } else {
          console.warn('Could not extract image path from URL:', imageUrl);
        }
      } catch (error) {
        console.error("Error deleting image:", error);
        // Continue with item deletion even if image deletion fails
      }
    }
    
    // Delete the item document
    await deleteDoc(itemRef);
    
    console.log(`Item ${itemId} deleted successfully`);
  } catch (error) {
    console.error("Error deleting item:", error);
    throw error;
  }
};

/**
 * Checks out an item to a student
 * @param {string} itemId - The ID of the item to check out
 * @param {string} studentId - The ID of the student checking out the item
 * @param {string} studentName - The name of the student checking out the item
 * @param {number} durationDays - Number of days for checkout duration (default 1)
 * @returns {Promise<Object>} - The updated item
 */
export const checkoutItem = async (itemId, studentId, studentName, durationDays = 1) => {
  try {
    // Get the item first to check if it's available
    const item = await getItemById(itemId);

    if (item.status !== 'Available') {
      throw new Error('Item is not available for checkout');
    }

    // Sanitize student name to prevent XSS
    const sanitizedStudentName = sanitizeInput.text(studentName || '');

    // First, ensure the student document exists
    const studentRef = doc(db, "students", studentId);
    const studentSnap = await getDoc(studentRef);

    if (!studentSnap.exists()) {
      // Create a new student document if it doesn't exist
      try {
        await setDoc(studentRef, {
          id: studentId,
          name: sanitizedStudentName,
          checkedOutItems: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      } catch (error) {
        console.error("Error creating student document:", error);
        throw new Error('Failed to create student record: ' + error.message);
      }
    }
    
    // After ensuring student exists, update the item status
    const itemRef = doc(db, "items", itemId);
    
    try {
      // Calculate the due date based on duration days
      const checkoutDate = new Date();
      const dueDate = new Date(checkoutDate);
      dueDate.setTime(dueDate.getTime() + (durationDays * 24 * 60 * 60 * 1000)); // Convert days to milliseconds
      
      await updateDoc(itemRef, {
        status: 'Checked Out',
        currentHolderId: studentId,
        currentHolderName: sanitizedStudentName,
        lastCheckoutTime: checkoutDate.toISOString(),
        dueDate: dueDate.toISOString(),
        checkoutDurationDays: durationDays
      });
    } catch (error) {
      console.error("Error updating item status:", error);
      throw new Error('Failed to update item status: ' + error.message);
    }
    
    // Then add to checkout history
    try {
      const checkoutDate = new Date();
      const dueDate = new Date(checkoutDate);
      dueDate.setTime(dueDate.getTime() + (durationDays * 24 * 60 * 60 * 1000)); // Convert days to milliseconds
      
      const historyRef = collection(db, "items", itemId, "checkoutHistory");
      await addDoc(historyRef, {
        studentId: studentId,
        studentName: sanitizedStudentName,
        action: 'checkout',
        timestamp: checkoutDate.toISOString(),
        timeOut: checkoutDate.toISOString(),
        dueDate: dueDate.toISOString(),
        durationDays: durationDays
      });
    } catch (error) {
      console.error("Error adding to checkout history:", error);
      // Don't throw here, continue with the process
    }
    
    // Finally, update the student's checked out items list
    try {
      await updateDoc(studentRef, {
        checkedOutItems: arrayUnion(itemId),
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error("Error updating student's checked out items:", error);
      throw new Error('Failed to update student record: ' + error.message);
    }
    
    return await getItemById(itemId);
  } catch (error) {
    console.error("Error checking out item:", error);
    throw error;
  }
};

/**
 * Checks out an item to a student with an explicit due date.
 * @param {string} itemId - The ID of the item to check out.
 * @param {string} studentId - The ID of the student checking out the item.
 * @param {string} studentName - The name of the student checking out the item.
 * @param {string} dueDate - The explicit due date for the checkout in ISO string format (e.g., "2023-12-31T23:59:59.999Z").
 * @returns {Promise<Object>} - The updated item.
 */
export const checkoutItemWithDue = async (itemId, studentId, studentName, dueDate) => {
  try {
    const item = await getItemById(itemId);
    if (item.status !== 'Available') {
      throw new Error('Item is not available for checkout');
    }

    // Sanitize student name to prevent XSS
    const sanitizedStudentName = sanitizeInput.text(studentName || '');

    const studentRef = doc(db, "students", studentId);
    const studentSnap = await getDoc(studentRef);
    if (!studentSnap.exists()) {
      await setDoc(studentRef, {
        id: studentId,
        name: sanitizedStudentName,
        checkedOutItems: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }

    const itemRef = doc(db, "items", itemId);
    const checkoutDate = new Date();

    await updateDoc(itemRef, {
      status: 'Checked Out',
      currentHolderId: studentId,
      currentHolderName: sanitizedStudentName,
      lastCheckoutTime: checkoutDate.toISOString(),
      dueDate: new Date(dueDate).toISOString(),
      checkoutDurationDays: null
    });

    try {
      const historyRef = collection(db, "items", itemId, "checkoutHistory");
      await addDoc(historyRef, {
        studentId: studentId,
        studentName: sanitizedStudentName,
        action: 'checkout',
        timestamp: checkoutDate.toISOString(),
        timeOut: checkoutDate.toISOString(),
        dueDate: new Date(dueDate).toISOString()
      });
    } catch {}

    try {
      await updateDoc(studentRef, {
        checkedOutItems: arrayUnion(itemId),
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      throw new Error('Failed to update student record: ' + error.message);
    }

    return await getItemById(itemId);
  } catch (error) {
    console.error("Error checking out item with due:", error);
    throw error;
  }
};

/**
 * Checks in an item
 * @param {string} itemId - The ID of the item to check in
 * @param {string} studentId - The ID of the student checking in the item
 * @param {string} condition - Optional condition notes
 * @returns {Promise<Object>} - The updated item
 */
export const checkinItem = async (itemId, studentId, condition = "") => {
try {
// Get the item first to check if it's checked out
const item = await getItemById(itemId);
 
if (item.status !== 'Checked Out') {
throw new Error('Item is not currently checked out');
}
 
if (item.currentHolderId !== studentId) {
throw new Error('Item is checked out to a different student');
}
 
const itemRef = doc(db, "items", itemId);
 
// Update the item status
try {
await updateDoc(itemRef, {
status: 'Available',
currentHolderId: null,
currentHolderName: null,
lastCheckinTime: new Date().toISOString(),
condition: condition || item.condition || null,
});
} catch (error) {
console.error("Error updating item status during check-in:", error);
throw new Error('Failed to update item status: ' + error.message);
}
 
// Find the latest checkout entry for this item and update it
try {
const historyRef = collection(db, "items", itemId, "checkoutHistory");
const q = query(historyRef, where("studentId", "==", studentId), where("action", "==", "checkout"));
const querySnapshot = await getDocs(q);
 
// Convert querySnapshot to array and sort by timestamp (most recent first)
const checkouts = [];
querySnapshot.forEach((doc) => {
checkouts.push({ id: doc.id, ...doc.data() });
});
 
checkouts.sort((a, b) => {
return new Date(b.timestamp) - new Date(a.timestamp);
});
 
// Update the most recent checkout record
if (checkouts.length > 0) {
const latestCheckoutId = checkouts[0].id;
const checkoutRef = doc(db, "items", itemId, "checkoutHistory", latestCheckoutId);
await updateDoc(checkoutRef, {
returnTimestamp: new Date().toISOString(),
condition: condition || null
});
}
 
// Add new checkin entry
await addDoc(historyRef, {
studentId: studentId,
studentName: item.currentHolderName,
action: 'checkin',
timestamp: new Date().toISOString(),
condition: condition || null
});
} catch (error) {
console.error("Error updating checkout history:", error);
// Continue with the process even if history update fails
}
 
// Remove the item from the student's checked out items list
try {
const studentRef = doc(db, "students", studentId);
const studentSnap = await getDoc(studentRef);
 
if (studentSnap.exists()) {
await updateDoc(studentRef, {
checkedOutItems: arrayRemove(itemId),
updatedAt: new Date().toISOString()
});
} else {
console.warn("Student document not found during check-in. No update needed.");
}
} catch (error) {
console.error("Error updating student record during check-in:", error);
// Don't fail the checkin if student record update fails
}
 
return await getItemById(itemId);
} catch (error) {
console.error("Error checking in item:", error);
throw error;
}
};

/**
 * Gets checkout history for an item
 * @param {string} itemId - The ID of the item
 * @param {number} limit - Maximum number of records to fetch
 * @returns {Promise<Array>} - Checkout history
 */
export const getItemCheckoutHistory = async (itemId, limit = 10) => {
  try {
    const checkoutsQuery = query(
      collection(db, "items", itemId, "checkoutHistory")
    );
    
    const querySnapshot = await getDocs(checkoutsQuery);
    const history = [];
    
    querySnapshot.forEach((doc) => {
      history.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort by timestamp descending (newest first)
    history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    return history.slice(0, limit);
  } catch (error) {
    console.error("Error getting checkout history:", error);
    throw error;
  }
};

/**
 * Gets items checked out by a student
 * @param {string} studentId - The ID of the student
 * @returns {Promise<Array>} - Items checked out
 */
export const getStudentCheckedOutItems = async (studentId) => {
  try {
    const q = query(
      collection(db, "items"),
      where("currentHolderId", "==", studentId),
      where("status", "==", "Checked Out")
    );
    
    const querySnapshot = await getDocs(q);
    const items = [];
    
    querySnapshot.forEach((doc) => {
      items.push({ id: doc.id, ...doc.data() });
    });
    
    return items;
  } catch (error) {
    console.error("Error getting student's checked out items:", error);
    throw error;
  }
};

/**
 * Gets all checked out items
 * @returns {Promise<Array>} - All checked out items
 */
export const getAllCheckedOutItems = async () => {
  try {
    const q = query(
      collection(db, 'items'),
      where('status', '==', 'Checked Out')
    );
    
    const querySnapshot = await getDocs(q);
    const items = [];
    
    querySnapshot.forEach((doc) => {
      items.push({ id: doc.id, ...doc.data() });
    });
    
    return items;
  } catch (error) {
    console.error('Error getting checked out items:', error);
    throw error;
  }
};

/**
 * Finds a student by their PID
 * @param {string} pid - The PID to search for
 * @returns {Promise<Object|null>} - Student data if found, null otherwise
 */
export const getStudentByPid = async (pid) => {
  try {
    const studentsRef = collection(db, 'students');
    const q = query(studentsRef, where('pid', '==', pid));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error finding student by PID:', error);
    throw error;
  }
};

/**
 * Records consumable usage and updates stock
 * @param {string} itemId - The ID of the consumable item
 * @param {number} amountUsed - Amount of consumable used
 * @param {string} userId - ID of the user using the consumable
 * @param {string} userName - Name of the user
 * @param {string} pid - PID of the user (if guest)
 * @param {string} notes - Optional notes about usage
 * @returns {Promise<Object>} - Usage record
 */
export const recordConsumableUsage = async (itemId, amountUsed, { userId = null, userName = null, pid = null, notes = '' }) => {
  try {
    const itemRef = doc(db, "items", itemId);
    const itemSnap = await getDoc(itemRef);
    
    if (!itemSnap.exists()) {
      throw new Error("Item not found");
    }
    
    const itemData = itemSnap.data();
    
    if (itemData.itemType !== 'consumable') {
      throw new Error("Item is not a consumable");
    }
    
    const currentStock = parseFloat(itemData.currentStock) || 0;
    const usageAmount = parseFloat(amountUsed);
    
    if (usageAmount <= 0) {
      throw new Error("Usage amount must be greater than 0");
    }
    
    if (usageAmount > currentStock) {
      throw new Error(`Not enough stock available. Current stock: ${currentStock} ${itemData.unit}`);
    }
    
    const newStock = currentStock - usageAmount;
    
    // Update the item's current stock
    await updateDoc(itemRef, {
      currentStock: newStock,
      lastUsedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    // Record usage history
    const usageHistoryRef = collection(db, "items", itemId, "usageHistory");
    const usageRecord = {
      userId: userId || null,
      userName: userName ? sanitizeInput.text(userName) : null,
      pid: pid || null,
      amountUsed: usageAmount,
      stockBefore: currentStock,
      stockAfter: newStock,
      unit: itemData.unit,
      notes: sanitizeInput.text(notes || ''),
      timestamp: new Date().toISOString(),
      cost: itemData.costPerUnit ? (usageAmount * itemData.costPerUnit) : null
    };
    
    const usageDocRef = await addDoc(usageHistoryRef, usageRecord);
    
    return { id: usageDocRef.id, ...usageRecord };
  } catch (error) {
    console.error("Error recording consumable usage:", error);
    throw error;
  }
};

/**
 * Gets usage history for a consumable item
 * @param {string} itemId - The ID of the consumable item
 * @param {number} limit - Maximum number of records to fetch
 * @returns {Promise<Array>} - Usage history
 */
export const getConsumableUsageHistory = async (itemId, limit = 20) => {
  try {
    const usageHistoryRef = collection(db, "items", itemId, "usageHistory");
    const usageHistoryQuery = query(usageHistoryRef);
    
    const querySnapshot = await getDocs(usageHistoryQuery);
    const history = [];
    
    querySnapshot.forEach((doc) => {
      history.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort by timestamp descending (newest first)
    history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    return history.slice(0, limit);
  } catch (error) {
    console.error("Error getting consumable usage history:", error);
    throw error;
  }
};

/**
 * Restocks a consumable item
 * @param {string} itemId - The ID of the consumable item
 * @param {number} amountAdded - Amount to add to stock
 * @param {string} notes - Optional notes about restocking
 * @returns {Promise<Object>} - Updated item
 */
export const restockConsumable = async (itemId, amountAdded, notes = '') => {
  try {
    const itemRef = doc(db, "items", itemId);
    const itemSnap = await getDoc(itemRef);
    
    if (!itemSnap.exists()) {
      throw new Error("Item not found");
    }
    
    const itemData = itemSnap.data();
    
    if (itemData.itemType !== 'consumable') {
      throw new Error("Item is not a consumable");
    }
    
    const currentStock = parseFloat(itemData.currentStock) || 0;
    const addAmount = parseFloat(amountAdded);
    
    if (addAmount <= 0) {
      throw new Error("Restock amount must be greater than 0");
    }
    
    const newStock = currentStock + addAmount;
    
    // Update the item's current stock
    await updateDoc(itemRef, {
      currentStock: newStock,
      lastRestockedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    // Record restock history
    const usageHistoryRef = collection(db, "items", itemId, "usageHistory");
    const restockRecord = {
      userId: 'admin',
      userName: 'Admin',
      pid: null,
      amountUsed: -addAmount, // Negative to indicate restocking
      stockBefore: currentStock,
      stockAfter: newStock,
      unit: itemData.unit,
      notes: sanitizeInput.text(notes || 'Restocked'),
      timestamp: new Date().toISOString(),
      isRestock: true
    };
    
    await addDoc(usageHistoryRef, restockRecord);
    
    return await getItemById(itemId);
  } catch (error) {
    console.error("Error restocking consumable:", error);
    throw error;
  }
};

/**
 * Gets all consumables with low stock alerts
 * @returns {Promise<Array>} - Items with low stock
 */
export const getLowStockConsumables = async () => {
  try {
    const q = query(
      collection(db, 'items'),
      where('itemType', '==', 'consumable')
    );
    
    const querySnapshot = await getDocs(q);
    const lowStockItems = [];
    
    querySnapshot.forEach((doc) => {
      const data = { id: doc.id, ...doc.data() };
      const currentStock = parseFloat(data.currentStock) || 0;
      const lowStockAlert = parseFloat(data.lowStockAlert) || 0;
      
      if (lowStockAlert > 0 && currentStock <= lowStockAlert) {
        lowStockItems.push({
          ...data,
          stockPercentage: data.initialStock ? (currentStock / data.initialStock) * 100 : 0
        });
      }
    });
    
    // Sort by stock percentage (lowest first)
    lowStockItems.sort((a, b) => a.stockPercentage - b.stockPercentage);
    
    return lowStockItems;
  } catch (error) {
    console.error('Error getting low stock consumables:', error);
    throw error;
  }
};

/**
 * Automatically resets expired checkouts to Available status.
 * Checks all items with 'Checked Out' status and if dueDate has passed,
 * sets them back to Available.
 * @returns {Promise<Array>} - Array of items that were reset
 */
export const resetExpiredCheckouts = async () => {
  try {
    const q = query(
      collection(db, 'items'),
      where('status', '==', 'Checked Out')
    );

    const querySnapshot = await getDocs(q);
    const resetItems = [];
    const now = new Date();

    for (const docSnap of querySnapshot.docs) {
      const itemData = { id: docSnap.id, ...docSnap.data() };

      // Check if dueDate exists and has passed
      if (itemData.dueDate) {
        const dueDate = new Date(itemData.dueDate);

        if (dueDate < now) {
          // Due date has passed, reset item to Available
          const itemRef = doc(db, 'items', docSnap.id);

          // Store previous holder info for history
          const previousHolderId = itemData.currentHolderId;
          const previousHolderName = itemData.currentHolderName;

          // Update item status to Available
          await updateDoc(itemRef, {
            status: 'Available',
            currentHolderId: null,
            currentHolderName: null,
            dueDate: null,
            checkoutDurationDays: null,
            autoReturnedAt: now.toISOString(),
            updatedAt: now.toISOString()
          });

          // Add to checkout history as auto-return
          try {
            const historyRef = collection(db, 'items', docSnap.id, 'checkoutHistory');
            await addDoc(historyRef, {
              studentId: previousHolderId,
              studentName: previousHolderName,
              action: 'auto-return',
              timestamp: now.toISOString(),
              timeIn: now.toISOString(),
              notes: 'Automatically returned - checkout period expired'
            });
          } catch (historyError) {
            console.error('Error adding auto-return to history:', historyError);
          }

          // Update student's checked out items list
          if (previousHolderId) {
            try {
              const studentRef = doc(db, 'students', previousHolderId);
              const studentSnap = await getDoc(studentRef);

              if (studentSnap.exists()) {
                await updateDoc(studentRef, {
                  checkedOutItems: arrayRemove(docSnap.id)
                });
              }
            } catch (studentError) {
              console.error('Error updating student record:', studentError);
            }
          }

          resetItems.push({
            id: docSnap.id,
            name: itemData.name,
            previousHolder: previousHolderName,
            dueDate: itemData.dueDate
          });

          console.log(`Auto-returned item: ${itemData.name} (was held by ${previousHolderName})`);
        }
      }
    }

    if (resetItems.length > 0) {
      console.log(`Automatically reset ${resetItems.length} expired checkout(s)`);
    }

    return resetItems;
  } catch (error) {
    console.error('Error resetting expired checkouts:', error);
    throw error;
  }
};
